import { useState, lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { LanguageSelector } from '../components/LanguageSelector';
import { ParentHome } from './ParentHome';
import { api } from '../lib/api';
import { showToast } from '../components/Toast';
import type { Feedback, StopChangeRequest, EmergencyContact } from '../lib/types';

const MultiChildQR = lazy(() => import('../components/qr/MultiChildQR').then(m => ({ default: m.MultiChildQR })));
const ParentBusScanner = lazy(() => import('../components/qr/ParentBusScanner').then(m => ({ default: m.ParentBusScanner })));

interface ParentPortalProps {
  onLogout: () => void;
  userName: string;
}

type Tab = 'map' | 'history' | 'feedback' | 'contacts' | 'qr' | 'scan-bus';

export function ParentPortal({ onLogout, userName }: ParentPortalProps) {
  const { t } = useTranslation('parent');
  const [tab, setTab] = useState<Tab>('map');
  const [history, setHistory] = useState<any[]>([]);
  const [feedback, setFeedback] = useState<Feedback[]>([]);
  const [contacts, setContacts] = useState<EmergencyContact[]>([]);
  const [loading, setLoading] = useState(false);

  const loadTab = async (t: Tab) => {
    setTab(t);
    if (t === 'map') return;
    setLoading(true);
    try {
      switch (t) {
        case 'history': { const r = await api.getTripHistory(); setHistory(r.history); break; }
        case 'feedback': { const r = await api.getMyFeedback(); setFeedback(r.feedback); break; }
        case 'contacts': { const r = await api.getEmergencyContacts(); setContacts(r.contacts); break; }
      }
    } catch { showToast('Failed to load data'); } finally { setLoading(false); }
  };

  if (tab === 'map') {
    return (
      <div className="parent-portal">
        <div className="parent-nav">
          <button className="tab-btn active" onClick={() => setTab('map')}>{t('tab_live_map')}</button>
          <button className="tab-btn" onClick={() => setTab('scan-bus')}>Scan Bus</button>
          <button className="tab-btn" onClick={() => setTab('qr')}>{t('tab_qr_code')}</button>
          <button className="tab-btn" onClick={() => loadTab('history')}>{t('tab_history')}</button>
          <button className="tab-btn" onClick={() => loadTab('feedback')}>{t('tab_feedback')}</button>
          <button className="tab-btn" onClick={() => loadTab('contacts')}>{t('tab_contacts')}</button>
        </div>
        <ParentHome onLogout={onLogout} userName={userName} />
      </div>
    );
  }

  return (
    <div className="parent-portal">
      <div className="parent-nav">
        <button className="tab-btn" onClick={() => setTab('map')}>{t('tab_live_map')}</button>
        <button className={`tab-btn ${tab === 'scan-bus' ? 'active' : ''}`} onClick={() => setTab('scan-bus')}>Scan Bus</button>
        <button className={`tab-btn ${tab === 'qr' ? 'active' : ''}`} onClick={() => setTab('qr')}>{t('tab_qr_code')}</button>
        <button className={`tab-btn ${tab === 'history' ? 'active' : ''}`} onClick={() => loadTab('history')}>{t('tab_history')}</button>
        <button className={`tab-btn ${tab === 'feedback' ? 'active' : ''}`} onClick={() => loadTab('feedback')}>{t('tab_feedback')}</button>
        <button className={`tab-btn ${tab === 'contacts' ? 'active' : ''}`} onClick={() => loadTab('contacts')}>{t('tab_contacts')}</button>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          <LanguageSelector compact />
          <span style={{ marginRight: 12, color: 'var(--text-muted)', fontSize: 13 }}>{userName}</span>
          <button className="btn-logout" onClick={onLogout}>{t('logout', { ns: 'common' })}</button>
        </div>
      </div>

      <div style={{ padding: 16 }}>
        {tab === 'scan-bus' && (
          <Suspense fallback={<div className="admin-loading">Loading scanner...</div>}>
            <h2>Verify Bus</h2>
            <p style={{ color: 'var(--text-muted)', marginBottom: 16 }}>Scan the QR code on the bus to confirm it's the right one for your child.</p>
            <ParentBusScanner />
          </Suspense>
        )}

        {tab === 'qr' && (
          <Suspense fallback={<div className="admin-loading">Loading QR...</div>}>
            <h2>{t('qr_title')}</h2>
            <p style={{ color: 'var(--text-muted)', marginBottom: 16 }}>{t('qr_subtitle')}</p>
            <MultiChildQR />
          </Suspense>
        )}

        {loading && <div className="admin-loading">Loading...</div>}

        {tab === 'history' && !loading && (
          <div>
            <h2>Trip History (Last 30 Days)</h2>
            <table className="admin-table">
              <thead><tr><th>Student</th><th>Bus</th><th>Type</th><th>Time</th></tr></thead>
              <tbody>
                {history.map((h: any, i: number) => (
                  <tr key={i}>
                    <td>{h.student_name}</td><td>{h.bus_number}</td>
                    <td>{h.type}</td><td>{new Date((h.recorded_at || h.created_at || '').replace(' ', 'T')).toLocaleString()}</td>
                  </tr>
                ))}
                {history.length === 0 && <tr><td colSpan={4} className="admin-empty">No trip history</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {tab === 'feedback' && !loading && (
          <div>
            <h2>My Feedback</h2>
            <form className="admin-form" onSubmit={async (e) => {
              e.preventDefault();
              const fd = new FormData(e.currentTarget);
              await api.submitFeedback({
                type: fd.get('type'), subject: fd.get('subject'), message: fd.get('message'),
              });
              const r = await api.getMyFeedback();
              setFeedback(r.feedback);
              (e.target as HTMLFormElement).reset();
            }}>
              <div className="admin-form-row">
                <div className="form-group"><label>Type</label>
                  <select name="type"><option value="suggestion">Suggestion</option><option value="complaint">Complaint</option><option value="appreciation">Appreciation</option><option value="other">Other</option></select>
                </div>
                <div className="form-group"><label>Subject</label><input type="text" name="subject" /></div>
              </div>
              <div className="form-group"><label>Message</label><textarea name="message" required rows={3} style={{ width: '100%', padding: '10px', background: 'var(--bg-input)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, color: 'var(--text)', fontSize: 14 }} /></div>
              <button type="submit" className="btn-sm btn-primary">Submit</button>
            </form>

            <div className="alert-list" style={{ marginTop: 16 }}>
              {feedback.map(f => (
                <div key={f.id} className="alert-item alert-info">
                  <div className="alert-header">
                    <span className={`admin-badge badge-blue`}>{f.type}</span>
                    <span className={`admin-badge ${f.status === 'resolved' ? 'badge-green' : 'badge-red'}`}>{f.status}</span>
                    <span className="alert-time">{new Date(f.created_at).toLocaleString()}</span>
                  </div>
                  {f.subject && <div className="alert-title">{f.subject}</div>}
                  <div className="alert-message">{f.message}</div>
                  {f.resolution_notes && <div className="form-success" style={{ marginTop: 8 }}>Resolution: {f.resolution_notes}</div>}
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === 'contacts' && !loading && (
          <div>
            <h2>Emergency Contacts</h2>
            <div className="admin-cards">
              {contacts.map(c => (
                <div key={c.id} className="admin-card">
                  <div className="admin-card-header">
                    <span className="admin-card-title">{c.name}</span>
                    {c.is_primary ? <span className="admin-badge badge-green">Primary</span> : null}
                  </div>
                  <div className="admin-card-body">
                    <div className="admin-card-row"><span>Relationship</span><span>{c.relationship}</span></div>
                    <div className="admin-card-row"><span>Phone</span><span>{c.phone}</span></div>
                    <div className="admin-card-row"><span>Student</span><span>{c.student_name}</span></div>
                  </div>
                  <button className="btn-sm btn-danger" style={{ marginTop: 8 }} onClick={async () => {
                    await api.deleteEmergencyContact(c.id);
                    setContacts(prev => prev.filter(x => x.id !== c.id));
                  }}>Remove</button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
